import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/common/Badges";
import { LayoutDashboard, Store, Building2, Package, ShoppingCart, Warehouse, Target, History, BarChart3, CheckSquare, Upload, LogOut, ChevronDown, Users, Bell, FileCode2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "regional_manager", "store_user", "warehouse"] },
  { to: "/order", label: "Place Order", icon: ShoppingCart, roles: ["admin", "regional_manager", "store_user"] },
  { to: "/history", label: "Orders", icon: History, roles: ["admin", "regional_manager", "store_user", "warehouse"] },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, roles: ["admin", "regional_manager", "warehouse"] },
  { to: "/products", label: "Products", icon: Package, roles: ["admin", "regional_manager", "store_user", "warehouse"] },
  { to: "/warehouse", label: "Warehouse", icon: Warehouse, roles: ["admin", "regional_manager", "warehouse"] },
  { to: "/stores", label: "Stores", icon: Store, roles: ["admin", "regional_manager", "store_user"] },
  { to: "/dcs", label: "Distribution Centers", icon: Building2, roles: ["admin", "regional_manager", "warehouse"] },
  { to: "/forecasting", label: "Forecasting", icon: Target, roles: ["admin", "regional_manager", "store_user"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "regional_manager", "warehouse"] },
  { to: "/import", label: "Import", icon: Upload, roles: ["admin", "regional_manager"] },
  { to: "/oracle", label: "Oracle XML", icon: FileCode2, roles: ["admin", "regional_manager", "warehouse"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/notifications", label: "Notifications", icon: Bell, roles: ["admin", "regional_manager", "store_user", "warehouse"], hidden: true },
];

function NotifBell() {
  const nav = useNavigate();
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => api.get("/notifications", { params: { limit: 1 } }).then((r) => setN(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("notif-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("notif-refresh", load); };
  }, []);
  return (
    <button data-testid="notifications-bell" onClick={() => nav("/notifications")} title="Notifications"
      className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600">
      <Bell className="h-4 w-4" />
      {n > 0 && <span data-testid="notifications-unread" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">{n > 99 ? "99+" : n}</span>}
    </button>
  );
}

export default function AppShell() {
  const { user, logout, currency, setCurrency } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role) && !n.hidden);
  return (
    <div className="min-h-screen bg-[#F6F7F9] text-slate-800">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between gap-2 px-3 sm:gap-4 sm:px-6">
          <div className="flex items-center gap-4">
            <img src="/brand/ep-50-dark.png" alt="EP Group — 50 years of delivering excellence" data-testid="ep-group-logo" className="h-7 w-auto sm:h-10" />
            <div className="hidden border-l border-slate-200 pl-4 leading-tight md:block">
              <p className="font-display text-base font-extrabold tracking-tight text-slate-900">Primark Replenishment Management System</p>
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Powered by EP Group</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/brand/primark-blue-wide.svg" alt="Primark" data-testid="primark-logo" className="mr-1 h-8 w-auto sm:h-10" />
            <div className="relative">
              <select data-testid="currency-switcher" value={currency} onChange={(e) => setCurrency(e.target.value)}
                className="h-9 appearance-none rounded-lg border border-slate-300 bg-white pl-2.5 pr-7 font-mono text-xs font-semibold text-slate-700 shadow-sm outline-none focus:border-red-500 sm:pl-3 sm:pr-8">
                <option value="GBP">£ GBP</option><option value="EUR">€ EUR</option><option value="USD">$ USD</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
            </div>
            <div className="hidden items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-1.5 sm:flex" data-testid="user-menu">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">
                {user.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="leading-tight">
                <p className="text-xs font-semibold text-slate-900" data-testid="user-name">{user.name}</p>
                <RoleBadge role={user.role} />
              </div>
            </div>
            <NotifBell />
            <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <nav className="mx-auto max-w-[1440px] overflow-x-auto px-4 sm:px-6" data-testid="main-nav">
          <div className="flex gap-1 whitespace-nowrap">
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => cn("flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900")}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
